import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AssistantKey, Namespace } from '@vendasta/protos/ai_assistants/v1alpha1';
import { NAMESPACE_CONFIG_TOKEN } from '../tokens';
import { NamespaceConfig } from '../interfaces/ai-assistant.interface';
import { NamespaceType } from '../utils/namespace-utils';

@Injectable()
export class AssistantKeyResolverService {
  private readonly namespaceConfig$: Observable<NamespaceConfig> | null = inject(NAMESPACE_CONFIG_TOKEN, {
    optional: true,
  });

  resolveFromRoute(activatedRoute: ActivatedRoute): Observable<AssistantKey> {
    return activatedRoute.paramMap.pipe(
      switchMap((paramMap) => this.resolveAssistantKeyFromParamMap(paramMap)),
    );
  }

  resolveAssistantKeyFromParamMap(paramMap: ParamMap): Observable<AssistantKey> {
    if (paramMap.has('namespaceType') && paramMap.has('namespaceId')) {
      return of(this.assistantKeyFromParamMap(paramMap));
    }

    const assistantId = paramMap.get('assistantId');
    if (assistantId && this.namespaceConfig$) {
      return this.namespaceConfig$.pipe(map((config) => ({ id: assistantId, namespace: config?.namespace })));
    }

    return of(this.assistantKeyFromParamMap(paramMap));
  }

  buildNamespaceFromParams(paramMap: ParamMap): Observable<Namespace | undefined> {
    const namespaceType = paramMap.get('namespaceType');
    const namespaceId = paramMap.get('namespaceId');

    if (namespaceType && namespaceId) {
      return of(this.buildNamespace(namespaceType as NamespaceType, namespaceId));
    }

    if (this.namespaceConfig$) {
      return this.namespaceConfig$.pipe(map((config) => config?.namespace));
    }

    return of(undefined);
  }

  private assistantKeyFromParamMap(paramMap: ParamMap): AssistantKey {
    return {
      id: paramMap.get('assistantId') || 'ASSISTANT-system',
      namespace: this.buildNamespace(paramMap.get('namespaceType') as NamespaceType | null, paramMap.get('namespaceId')),
    };
  }

  private buildNamespace(namespaceType: NamespaceType | null, namespaceId: string | null): Namespace | undefined {
    switch (namespaceType) {
      case 'GLOBAL':
        return { globalNamespace: {} };
      case 'SYSTEM':
        return { systemNamespace: {} };
      case 'ACCOUNT_GROUP':
        return { accountGroupNamespace: { accountGroupId: namespaceId ?? '' } };
      case 'PARTNER':
        return { partnerNamespace: { partnerId: namespaceId ?? '' } };
      default:
        return undefined;
    }
  }
}
